import { format, isSameDay, isSameMonth, isSameYear } from 'date-fns';

import type { HistoryItem } from '@poseidon/components/ContactDetail';
import type { HistoryStepType } from '@poseidon/components/HistoryStep';
import { interactionHistoryAction } from '@poseidon/components/ContactDetail';
import { getInteractionStepType } from '@poseidon/components/HistoryStep';
import { stripHtmlTags } from '@poseidon/shared-utils/html';

/**
 * Only what a step reads off a history entry. Every `HistoryItem` fits it (it's a structural subset), so the
 * canvas passes its entries straight in.
 */
export interface HistoryStepEntry {
  interaction: Pick<
    HistoryItem['interaction'],
    'odooInteractionAction' | 'interactionType' | 'userId' | 'createdAt' | 'subject' | 'body'
  >;
  interactionUser: { name: string | null; profilePicture?: string | null } | null;
  contact: { primaryFields: Partial<Record<string, { data: string }>>; primaryPictureUrl?: string | null } | null;
  documentView: { title: string } | null;
  cadence: { name: string | null } | null;
}

/** A face on a step: the contact, or the rep (you or a teammate) — with what the avatar falls back to. */
export type HistoryStepPerson =
  | { kind: 'contact'; firstName: string | null; lastName: string | null; pictureUrl: string | null }
  | { kind: 'rep'; userId: string; name: string | null; pictureUrl: string | null };

/** One canvas step as it reads: its icon kind, a one-line "who did what", a text glimpse, and when. */
export interface HistoryStepSummary {
  stepType: HistoryStepType;
  title: string;
  /** The first words of the newest thing said on the step — on a conversation, prefixed with who said it. */
  snippet: string | null;
  /** The step's first and last moments (the same for a single entry). */
  from: Date;
  at: Date;
  /** How many entries the step holds: 1, or the messages a conversation step folds together. */
  count: number;
  /** The newest thing on the step is something YOU did (sent, invited, called) — drawn on a tinted tile. A
   *  teammate's own outreach stays plain: it isn't yours. */
  fromYou: boolean;
  /**
   * Whose faces the step shows, the newest speaker last (drawn on top): one person, or both sides of a
   * conversation. Empty for an event nobody in particular did — a stage change, a campaign start — which
   * shows its icon instead.
   */
  people: HistoryStepPerson[];
}

/** A step on the canvas: one entry, or a run of messages folded into one conversation. Entries oldest first. */
export interface HistoryStepGroup<E extends HistoryStepEntry> {
  entries: E[];
  summary: HistoryStepSummary;
}

const SNIPPET_CHARS = 90;

/** Messages further apart than this start a new step: a reply days later is the same conversation, a nudge weeks later isn't. */
const CONVERSATION_GAP_MS = 3 * 24 * 60 * 60 * 1000;

type Channel = 'message' | 'inmail' | 'email';

const CHANNEL_OF: Partial<Record<string, Channel>> = {
  message_sent: 'message',
  message_received: 'message',
  inmail_sent: 'inmail',
  inmail_received: 'inmail',
  email_sent: 'email',
  email_received: 'email',
};

const CHANNEL_NOUN: Record<Channel, string> = { message: 'messages', inmail: 'InMails', email: 'emails' };

/** What the contact did, as opposed to the rep. */
const BY_CONTACT = new Set([
  'message_received',
  'inmail_received',
  'inmail_declined',
  'email_received',
  'connection_accepted',
  'phone_received',
  'document_viewed',
]);

/** What a rep does by hand (or a sequence does as them) — drawn with the rep's face. */
const BY_REP = new Set([
  'message_sent',
  'inmail_sent',
  'email_sent',
  'connection_sent',
  'phone_call_sent',
  'comment_on_post',
  'visit',
  'follow',
  'like_posts',
]);

const CARRIES_TEXT = new Set([
  'message_sent',
  'message_received',
  'inmail_sent',
  'inmail_received',
  'email_sent',
  'email_received',
  'comment_on_post',
]);

/** Plain, single-line, length-capped text from a stored HTML / text body. */
function glimpse(html: string | null | undefined): string | null {
  const text = stripHtmlTags(html).replace(/\s+/g, ' ').trim();
  if (text === '') return null;
  return text.length > SNIPPET_CHARS ? `${text.slice(0, SNIPPET_CHARS - 1).trimEnd()}…` : text;
}

const channelOf = (entry: HistoryStepEntry): Channel | undefined => CHANNEL_OF[interactionHistoryAction(entry)];
const isByContact = (entry: HistoryStepEntry) => BY_CONTACT.has(interactionHistoryAction(entry));
const contactName = (entry: HistoryStepEntry) => entry.contact?.primaryFields?.firstName?.data || 'They';
const repName = (entry: HistoryStepEntry, currentUserId: string) =>
  entry.interaction.userId === currentUserId ? 'You' : (entry.interactionUser?.name ?? 'A teammate');
/** The face for an entry: the contact for what they did, the rep for what the rep did, none for an event. */
function personOf(entry: HistoryStepEntry): HistoryStepPerson | null {
  const action = interactionHistoryAction(entry);
  if (BY_CONTACT.has(action)) {
    const fields = entry.contact?.primaryFields;
    return {
      kind: 'contact',
      firstName: fields?.firstName?.data || null,
      lastName: fields?.lastName?.data || null,
      pictureUrl: entry.contact?.primaryPictureUrl ?? null,
    };
  }
  if (BY_REP.has(action)) {
    return {
      kind: 'rep',
      userId: entry.interaction.userId,
      name: entry.interactionUser?.name ?? null,
      pictureUrl: entry.interactionUser?.profilePicture ?? null,
    };
  }
  return null;
}

/** Everyone on a run, once each, ordered by when they LAST spoke — so the newest speaker comes last. */
function peopleOf(run: HistoryStepEntry[]): HistoryStepPerson[] {
  const byKey = new Map<string, HistoryStepPerson>();
  for (const entry of run) {
    const person = personOf(entry);
    if (person == null) continue;
    const key = person.kind === 'contact' ? 'contact' : `rep:${person.userId}`;
    byKey.delete(key);
    byKey.set(key, person);
  }
  return [...byKey.values()];
}

/** A teammate's first name, short enough for a title or a speaker prefix — null when they have no name. */
const teammateFirstName = (entry: HistoryStepEntry) => entry.interactionUser?.name?.trim().split(/\s+/)[0] || null;

/**
 * Whose account an entry belongs to, when it isn't yours: the teammate's first name — so what the contact did
 * says who it was with ("Michael accepted Danilo's invite"). Null for your own.
 */
const otherRep = (entry: HistoryStepEntry, currentUserId: string) =>
  entry.interaction.userId === currentUserId ? null : (teammateFirstName(entry) ?? 'a teammate');

/** Who said a line of a conversation, short: the contact's first name, "You", or the teammate's first name. */
const speakerName = (entry: HistoryStepEntry, currentUserId: string) => {
  if (isByContact(entry)) return contactName(entry);
  if (entry.interaction.userId === currentUserId) return 'You';
  return teammateFirstName(entry) ?? 'Teammate';
};

const possessive = (name: string) => `${name}'s`;

/** Our side did it, and that side is you. */
const isFromYou = (entry: HistoryStepEntry, currentUserId: string) =>
  BY_REP.has(interactionHistoryAction(entry)) && entry.interaction.userId === currentUserId;

/**
 * The step text for ONE history entry on the sequence canvas. The full entry — Respond, the transcript,
 * the document — opens from the step; this is only enough to recognise it while panning. `answersUs`: the
 * last word on this channel before it was ours, so a message from them is a reply rather than out of the blue.
 */
export function summarizeHistoryEntry(
  entry: HistoryStepEntry,
  currentUserId: string,
  answersUs = false,
): HistoryStepSummary {
  const action = interactionHistoryAction(entry);
  const i = entry.interaction;
  const who = repName(entry, currentUserId);
  const them = contactName(entry);
  // The teammate whose account it happened on — what the contact did names them, since it wasn't with you.
  const tm = otherRep(entry, currentUserId);
  const campaign = entry.cadence?.name ?? 'a campaign';
  const titles: Record<string, string> = {
    message_sent: `${who} sent a message`,
    message_received:
      tm == null
        ? answersUs
          ? `${them} replied`
          : `${them} sent a message`
        : answersUs
          ? `${them} replied to ${tm}`
          : `${them} messaged ${tm}`,
    inmail_sent: `${who} sent an InMail`,
    inmail_received: `${them} replied to ${tm == null ? 'your' : possessive(tm)} InMail`,
    inmail_declined: `${them} declined ${tm == null ? 'your' : possessive(tm)} InMail`,
    email_sent: `${who} sent an email`,
    email_received:
      tm == null
        ? answersUs
          ? `${them} replied by email`
          : `${them} sent an email`
        : answersUs
          ? `${them} replied to ${tm} by email`
          : `${them} emailed ${tm}`,
    connection_sent: `${who} sent an invite`,
    connection_accepted: `${them} accepted ${tm == null ? 'your' : possessive(tm)} invite`,
    phone_call_sent: `${who} called`,
    phone_received: tm == null ? `${them} called` : `${them} called ${tm}`,
    cadence_start: `Started ${campaign}`,
    campaign_start: `Started ${campaign}`,
    campaign_delete: `Removed from ${campaign}`,
    document_viewed: `${them} viewed ${entry.documentView?.title ?? 'a document'}`,
    comment_on_post: `${who} commented on a post`,
    stage_changed: i.subject != null && i.subject !== '' ? `Moved to ${i.subject}` : 'Stage changed',
    visit: `${who} visited their profile`,
    follow: `${who} followed them`,
    like_posts: `${who} liked their posts`,
    reminder_completed: 'Reminder done',
    step_failed: 'A step failed',
    step_skipped: 'A step was skipped',
    step_canceled: 'A step was canceled',
    meeting_booked: 'Meeting booked',
    interest_detected: 'Interest detected',
    interest_abandoned: 'Interest went stale',
    contact_unavailable: 'Contact unavailable',
    contact_available: 'Contact available again',
    cadence_state_changed: i.subject != null && i.subject !== '' ? i.subject : 'Campaign state changed',
  };
  return {
    stepType: getInteractionStepType(action),
    title: titles[action] ?? action.replace(/_/g, ' '),
    snippet: CARRIES_TEXT.has(action) ? (glimpse(i.body) ?? glimpse(i.subject)) : null,
    from: i.createdAt,
    at: i.createdAt,
    count: 1,
    fromYou: isFromYou(entry, currentUserId),
    people: peopleOf([entry]),
  };
}

/**
 * One step for a run of messages on one channel: who wrote, how many, and the newest words — prefixed with who
 * said them only when both sides spoke (one-sided, the title already says who).
 */
function summarizeConversation(
  run: HistoryStepEntry[],
  channel: Channel,
  currentUserId: string,
  answersUs: boolean,
): HistoryStepSummary {
  const first = run[0]!;
  const last = run[run.length - 1]!;
  const n = run.length;
  const noun = CHANNEL_NOUN[channel];
  const them = contactName(first);
  // A run is one rep's conversation — a teammate's, it says so.
  const tm = otherRep(first, currentUserId);
  const theirs = run.filter(isByContact).length;
  const title =
    theirs === n
      ? answersUs
        ? `${them} replied${tm == null ? '' : ` to ${tm}`} ${n} times`
        : `${them} sent${tm == null ? '' : ` ${tm}`} ${n} ${noun}`
      : theirs === 0
        ? `${repName(first, currentUserId)} sent ${n} ${noun}`
        : tm == null
          ? `${n} ${noun} with ${them}`
          : `${n} ${noun} between ${tm} and ${them}`;
  const text = glimpse(last.interaction.body) ?? glimpse(last.interaction.subject);
  return {
    stepType: getInteractionStepType(interactionHistoryAction(last)),
    title,
    snippet: text == null || theirs === 0 || theirs === n ? text : `${speakerName(last, currentUserId)}: ${text}`,
    from: first.interaction.createdAt,
    at: last.interaction.createdAt,
    count: n,
    fromYou: isFromYou(last, currentUserId),
    people: peopleOf(run),
  };
}

/**
 * The contact's history (oldest first) as canvas steps. Back-to-back messages on one channel, in one rep's
 * conversation, each within {@link CONVERSATION_GAP_MS} of the last, fold into ONE conversation step — so a
 * chat reads as one step, not a stack of identical "replied" rows. Anything else in between (a connection, a
 * call) breaks the run. Walking oldest first also tells a reply (the last word in that rep's conversation on
 * that channel was the rep's) from a message out of the blue.
 */
export function groupHistorySteps<E extends HistoryStepEntry>(
  entries: E[],
  currentUserId: string,
): HistoryStepGroup<E>[] {
  const runs: E[][] = [];
  for (const entry of entries) {
    const run = runs[runs.length - 1];
    const prev = run?.[run.length - 1];
    const channel = channelOf(entry);
    const continues =
      run != null &&
      prev != null &&
      channel != null &&
      channelOf(prev) === channel &&
      prev.interaction.userId === entry.interaction.userId &&
      entry.interaction.createdAt.getTime() - prev.interaction.createdAt.getTime() <= CONVERSATION_GAP_MS;
    if (continues) run.push(entry);
    else runs.push([entry]);
  }

  // Who had the last word so far in each rep's conversation on each channel — a message in Danilo's inbox
  // answers Danilo, never something you sent.
  const lastWord = new Map<string, 'us' | 'them'>();
  return runs.map((run) => {
    const first = run[0]!;
    const channel = channelOf(first);
    const conversation = `${channel}:${first.interaction.userId}`;
    const answersUs = channel != null && lastWord.get(conversation) === 'us';
    if (channel != null) lastWord.set(conversation, isByContact(run[run.length - 1]!) ? 'them' : 'us');
    const summary =
      run.length > 1 && channel != null
        ? summarizeConversation(run, channel, currentUserId, answersUs)
        : summarizeHistoryEntry(first, currentUserId, answersUs);
    return { entries: run, summary };
  });
}

/**
 * A step's date: the day, or the span a conversation ran over ("Aug 26–27", "Aug 26 – Sep 28"). The year
 * shows only off the current one, so loaded-older history doesn't read as this year's.
 */
export function stepDateLabel(from: Date, to: Date, now: Date = new Date()): string {
  const day = (d: Date) => format(d, isSameYear(d, now) ? 'MMM d' : 'MMM d, yyyy');
  if (isSameDay(from, to)) return day(to);
  if (isSameMonth(from, to)) return `${format(from, 'MMM d')}–${format(to, isSameYear(to, now) ? 'd' : 'd, yyyy')}`;
  return `${day(from)} – ${day(to)}`;
}
